/**
 * Homepage order-panel product preview mapping.
 *
 * Replace these files with real product photos when ready.
 * Keep filenames the same to avoid code changes.
 *
 * Expected paths (under public/):
 * - /images/products/gold-sea-moss-jar.png
 * - /images/products/gold-sea-moss-jar-300ml.png  (size-specific)
 * - /images/products/gold-sea-moss-jar-1000ml.png  (size-specific)
 * - /images/products/gold-sea-moss-pouch.png
 * - /images/products/purple-sea-moss-jar.png
 * - /images/products/purple-sea-moss-jar-300ml.png  (size-specific)
 * - /images/products/purple-sea-moss-jar-1000ml.png  (size-specific)
 * - /images/products/purple-sea-moss-pouch.png
 *
 * Optional `bySize` keys (300 | 500 | 1000) override the default when present.
 */
const P = process.env.PUBLIC_URL || "";

/** @typedef {{ src: string, alt: string, comingSoonLabel: string }} PreviewAsset */
/** @typedef {PreviewAsset & { bySize?: Partial<Record<300|500|1000, PreviewAsset>> }} PreviewPackAsset */

/**
 * @type {{
 *   gold: { jar: PreviewPackAsset, pouch: PreviewPackAsset },
 *   purple: { jar: PreviewPackAsset, pouch: PreviewPackAsset }
 * }}
 */
export const PRODUCT_PREVIEW_IMAGES = {
  gold: {
    jar: {
      src: `${P}/images/products/gold-sea-moss-jar.png`,
      alt: "Gold Sea Moss Gel in a glass jar",
      comingSoonLabel: "Gold jar photo coming soon",
      bySize: {
        300: {
          src: `${P}/images/products/gold-sea-moss-jar-300ml.png`,
          alt: "Gold Sea Moss Gel 300ml glass jar",
          comingSoonLabel: "Gold jar 300ml photo coming soon",
        },
        1000: {
          src: `${P}/images/products/gold-sea-moss-jar-1000ml.png`,
          alt: "Gold Sea Moss Gel 1L glass jar",
          comingSoonLabel: "Gold jar 1L photo coming soon",
        },
      },
    },
    pouch: {
      src: `${P}/images/products/gold-sea-moss-pouch.png`,
      alt: "Gold Sea Moss Gel foil pouch",
      comingSoonLabel: "Gold pouch photo coming soon",
    },
  },
  purple: {
    jar: {
      src: `${P}/images/products/purple-sea-moss-jar.png`,
      alt: "Purple Sea Moss Gel in a glass jar",
      comingSoonLabel: "Purple jar photo coming soon",
      bySize: {
        300: {
          src: `${P}/images/products/purple-sea-moss-jar-300ml.png`,
          alt: "Purple Sea Moss Gel 300ml glass jar",
          comingSoonLabel: "Purple jar 300ml photo coming soon",
        },
        1000: {
          src: `${P}/images/products/purple-sea-moss-jar-1000ml.png`,
          alt: "Purple Sea Moss Gel 1L glass jar",
          comingSoonLabel: "Purple jar 1L photo coming soon",
        },
      },
    },
    pouch: {
      src: `${P}/images/products/purple-sea-moss-pouch.png`,
      alt: "Purple Sea Moss Gel foil pouch",
      comingSoonLabel: "Purple pouch photo coming soon",
    },
  },
};

/**
 * @param {'gold'|'purple'} base
 * @param {'jar'|'pouch'} packaging
 * @param {300|500|1000} [sizeMl]
 * @returns {PreviewAsset}
 */
export function getProductPreview(base, packaging, sizeMl) {
  const colour = base === "purple" ? "purple" : "gold";
  const pack = packaging === "pouch" ? "pouch" : "jar";
  const entry = PRODUCT_PREVIEW_IMAGES[colour][pack];
  const sized = sizeMl != null && entry.bySize?.[sizeMl];
  if (sized?.src) return sized;
  return {
    src: entry.src,
    alt: entry.alt,
    comingSoonLabel: entry.comingSoonLabel,
  };
}
